/**
 * Locked Chart Card Component
 *
 * Generic locked card for charts/visualizations with blurred preview
 */

import { Lock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MetricTooltip } from "./metric-tooltip";

interface LockedChartCardProps {
  title: string;
  tooltip?: string;
  mockContent: React.ReactNode; // The mock/blurred content to show
  height?: string; // Optional height class
}

export function LockedChartCard({
  title,
  tooltip,
  mockContent,
  height = "h-[400px]",
}: LockedChartCardProps) {
  return (
    <Card
      className={`group relative overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 cursor-pointer ${height}`}
    >
      {/* Header - always clear */}
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
          {title}
          {tooltip && <MetricTooltip content={tooltip} />}
        </CardTitle>
      </CardHeader>

      {/* Blurred content preview */}
      <CardContent className="relative">
        <div className="blur-[3px] pointer-events-none select-none opacity-50">{mockContent}</div>
      </CardContent>

      {/* Premium overlay - shows on hover */}
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/90 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <Lock className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-sm font-semibold text-foreground">Premium Feature</p>
      </div>
    </Card>
  );
}
